import { useSnackbar } from "@components";
import { BaseUser } from "@models";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { UserToInvite } from "@types";
import { useState } from "react";
import { useInviteUserMutation } from "../../../../../../hooks";
import { emptyUser } from "../../utils";

const invitedUserMessageDictionary = (inviteeFirstName: string) => ({
  success: {
    text: `${inviteeFirstName} invited to organization.`,
    color: "green",
  },
  conflict: {
    text: "This user already exists.",
    color: "red",
  },
  error: {
    text: "Something went wrong, please try again later.",
    color: "red",
  },
});

type InviteUserModalProps = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onUserInvited?: (user: BaseUser) => void;
};

export const InviteUserModal = ({
  open,
  onClose,
  orgId,
  onUserInvited,
}: InviteUserModalProps) => {
  const [userToInvite, setUserToInvite] = useState<UserToInvite>(emptyUser);
  const [isInvitationLoading, setIsInvitationLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const inviteUserMutation = useInviteUserMutation(orgId);
  const { showSnackbar } = useSnackbar();

  const handleInviteUser = async () => {
    setIsInvitationLoading(true);
    setErrorMessage(null);

    try {
      const waitMutation = await inviteUserMutation.mutateAsync({
        inviteeEmail: userToInvite.email.trim(),
        inviteeFirstName: userToInvite.firstName.trim(),
        inviteeLastName: userToInvite.lastName.trim(),
      });

      const isMutationConflict = waitMutation.status === 409;
      const isMutationSuccessful = waitMutation.status === 200;

      const inviteeFirstName = userToInvite.firstName.trim();

      if (isMutationSuccessful) {
        const message = invitedUserMessageDictionary(inviteeFirstName).success;
        showSnackbar(message.text);
        if (waitMutation.data && onUserInvited) {
          onUserInvited(waitMutation.data);
        }
        // Reset form and close after a short delay
        setTimeout(() => {
          setUserToInvite(emptyUser);
          onClose();
        }, 500);
      } else if (isMutationConflict) {
        const message = invitedUserMessageDictionary(inviteeFirstName).conflict;
        setErrorMessage(message.text);
      } else {
        const message = invitedUserMessageDictionary(inviteeFirstName).error;
        setErrorMessage(message.text);
      }
    } catch (error) {
      const message = invitedUserMessageDictionary(
        userToInvite.firstName.trim()
      ).error;
      setErrorMessage(message.text);
    } finally {
      setIsInvitationLoading(false);
    }
  };

  const handleClose = () => {
    if (!isInvitationLoading) {
      setUserToInvite(emptyUser);
      setErrorMessage(null);
      onClose();
    }
  };

  const hasUserFilledAllInputs =
    !!userToInvite?.email &&
    !!userToInvite?.firstName &&
    !!userToInvite?.lastName;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="invite-user-dialog-title"
      sx={{
        "& .MuiPaper-root": {
          maxWidth: "500px",
          width: "100%",
        },
      }}
    >
      <DialogTitle id="invite-user-dialog-title">Invite User</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-3 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="First Name"
              size="small"
              fullWidth
              value={userToInvite.firstName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setUserToInvite({
                  ...userToInvite,
                  firstName: event.target.value,
                })
              }
            />
            <TextField
              label="Last Name"
              size="small"
              fullWidth
              value={userToInvite.lastName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setUserToInvite({
                  ...userToInvite,
                  lastName: event.target.value,
                })
              }
            />
            <TextField
              label="Email"
              size="small"
              fullWidth
              className="col-span-full"
              type="email"
              value={userToInvite.email}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setUserToInvite({
                  ...userToInvite,
                  email: event.target.value,
                })
              }
            />
          </div>
          {errorMessage && (
            <div className="text-red-600 text-sm">{errorMessage}</div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          disabled={isInvitationLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleInviteUser}
          variant="contained"
          color="primary"
          disabled={!hasUserFilledAllInputs || isInvitationLoading}
          startIcon={
            isInvitationLoading ? (
              <CircularProgress size={14} sx={{ color: "#fff" }} />
            ) : null
          }
        >
          Invite
        </Button>
      </DialogActions>
    </Dialog>
  );
};
